import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Diamond, Loader2, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SelectNative } from "@/components/ui/select-native";
import { apiFetch } from "@/lib/api";
import {
  useAssetsQuery,
  useModelsQuery,
  useStylePresetsQuery,
} from "@/lib/queries";
import { toast, usePendingStyleStore } from "@/stores/ui";
import { friendlyError } from "@/lib/errors";
import { cn } from "@/lib/utils";
import type {
  AspectRatio,
  Asset,
  CreateFreeJobResponse,
  GenerationModel,
  Quality,
  ResultAsset,
} from "@/lib/types";
import { PromptBox } from "@/components/free/PromptBox";
import { StyleCarousel } from "@/components/free/StyleCarousel";
import { ReferenceStrip, type Reference } from "@/components/free/ReferenceStrip";
import { ResultCard } from "@/components/shared/ResultCard";

// Per-model max upload size for the reference image (MB). OpenAI Images API
// accepts up to 25 MB for gpt-image-*; nano-banana via OneDone is tighter.
// Keep this map in sync with backend tolerance — used only for UI guardrails;
// the real ceiling is enforced upstream.
const MAX_REFERENCE_MB: Record<string, number> = {
  "gpt-image-1": 25,
  "gpt-image-2": 25,
  "nano-banana": 4,
};

const pickHighestQuality = (qualities: Quality[] = []): Quality => {
  if (qualities.includes("high")) return "high";
  if (qualities.includes("medium")) return "medium";
  if (qualities.includes("low")) return "low";
  return "high";
};

const STYLE_CAT_LABEL: Record<string, string> = {
  all: "全部",
  realistic: "写实",
  illustration: "插画",
  concept: "概念",
  design: "设计",
};

export function FreeView() {
  const qc = useQueryClient();

  const [prompt, setPrompt] = useState("");
  const [optimizePrompt, setOptimizePrompt] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [quality, setQuality] = useState<Quality>("high");
  const [aspect, setAspect] = useState<AspectRatio>("1:1");
  const [outputCount, setOutputCount] = useState(1);
  const [styleId, setStyleId] = useState<string | null>(null);
  const [styleCategory, setStyleCategory] = useState<string>("all");
  const [references, setReferences] = useState<Reference[]>([]);
  const [results, setResults] = useState<ResultAsset[]>([]);
  const [estimateCost, setEstimateCost] = useState(0);

  // queryKey + queryFn shape is locked inside these hooks (lib/queries.ts).
  // Multiple components sharing the same queryKey are guaranteed to receive
  // the same cached shape — eliminates the 2026-05-18 "d.filter is not a
  // function" crash where shape mismatch corrupted the cache.
  const modelsQuery = useModelsQuery();
  const { items: styles } = useStylePresetsQuery();
  const { items: assets } = useAssetsQuery();

  const models = modelsQuery.data ?? [];

  const currentModel = useMemo<GenerationModel | null>(
    () => models.find((m) => m.code === selectedModel) ?? null,
    [models, selectedModel]
  );

  // Auto-pick first available production model on first load.
  useEffect(() => {
    if (selectedModel || models.length === 0) return;
    setSelectedModel(models[0].code);
  }, [models, selectedModel]);

  // Consume pendingStyleStore: a style chosen from 风格广场 should be
  // auto-applied here with toast + scroll + pulse the moment styles load.
  const consumePendingStyle = usePendingStyleStore((s) => s.consume);
  const pendingStyleId = usePendingStyleStore((s) => s.styleId);
  useEffect(() => {
    if (!pendingStyleId || styles.length === 0) return;
    const id = consumePendingStyle();
    if (!id) return;
    setStyleId(id);
    const applied = styles.find((s) => s.style_preset_id === id);
    if (applied) {
      toast(`已套用风格：${applied.name}`);
      if (applied.category) setStyleCategory(applied.category);
    }
    requestAnimationFrame(() => {
      const card = document.querySelector<HTMLElement>(
        `[data-style-card="${id}"]`,
      );
      if (!card) return;
      card.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      card.classList.add("style-pulse");
      setTimeout(() => card.classList.remove("style-pulse"), 1400);
    });
  }, [pendingStyleId, styles, consumePendingStyle]);

  // Style categories + filtered list
  const styleCategories = useMemo(() => {
    const set = new Set<string>(["all"]);
    styles.forEach((s) => s.category && set.add(s.category));
    return Array.from(set);
  }, [styles]);
  const visibleStyles = useMemo(
    () => (styleCategory === "all" ? styles : styles.filter((s) => s.category === styleCategory)),
    [styles, styleCategory],
  );

  // ── Reference upload helper (R2 presigned PUT or local b64 fallback) ──
  async function uploadReferenceFile(file: File) {
    try {
      const dim = await readImageDimensions(file);
      const init = await apiFetch<{
        mode: "r2_presigned" | "local_b64";
        upload_url: string;
        upload_method: string;
        upload_headers?: Record<string, string>;
        file_url: string | null;
      }>("/api/v2/assets/upload-url", {
        method: "POST",
        body: {
          file_name: file.name,
          mime_type: file.type,
          file_size: file.size,
        },
      });

      let fileUrl: string;
      if (init.mode === "r2_presigned") {
        const res = await fetch(init.upload_url, {
          method: "PUT",
          headers: init.upload_headers ?? { "Content-Type": file.type },
          body: file,
        });
        if (!res.ok) throw new Error(`R2 上传失败: ${res.status}`);
        fileUrl = init.file_url!;
      } else {
        const b64 = await readFileAsBase64(file);
        const local = await apiFetch<{ file_url: string }>(
          "/api/v2/assets/upload-local",
          {
            method: "POST",
            body: {
              filename: file.name,
              mime_type: file.type,
              b64,
            },
          },
        );
        fileUrl = local.file_url;
      }

      const asset = await apiFetch<Asset>("/api/v2/assets", {
        method: "POST",
        body: {
          name: file.name.replace(/\.[^.]+$/, ""),
          asset_type: "source_product_image",
          file_url: fileUrl,
          thumbnail_url: fileUrl,
          mime_type: file.type,
          file_size: file.size,
          width: dim.width,
          height: dim.height,
          tags: [],
        },
      });

      setReferences((prev) => [
        ...prev,
        {
          asset_id: asset.asset_id,
          thumbnail_url: asset.thumbnail_url || asset.file_url || fileUrl,
          file_url: asset.file_url || fileUrl,
          name: asset.name || file.name.replace(/\.[^.]+$/, ""),
        },
      ]);
      qc.invalidateQueries({ queryKey: ["assets"] });
      toast("参考图已上传");
    } catch (e) {
      toast(e instanceof Error ? e.message : "上传失败");
    }
  }

  // Sync quality/aspect when model changes. Quality is hidden in the UI; when
  // a model supports multiple tiers we always submit the highest one.
  useEffect(() => {
    if (!currentModel) return;
    const nextQuality = pickHighestQuality(currentModel.supported_qualities as Quality[]);
    if (quality !== nextQuality) setQuality(nextQuality);
    if (
      currentModel.supported_aspects.length > 0 &&
      !currentModel.supported_aspects.includes(aspect)
    ) {
      setAspect(currentModel.supported_aspects[0] ?? "1:1");
    }
  }, [currentModel, quality, aspect]);

  // ── Cost estimate (debounced) ───────────────────────────────────
  useEffect(() => {
    if (!selectedModel) {
      setEstimateCost(0);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const r = await apiFetch<{ cost_credits: number }>(
          "/api/v2/billing/estimate-generation-cost",
          {
            method: "POST",
            body: {
              scenario_code: "free_creation",
              model_code: selectedModel,
              quality_tier: quality,
              output_count: outputCount,
            },
          }
        );
        setEstimateCost(r.cost_credits);
      } catch {
        setEstimateCost(0);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [selectedModel, quality, outputCount]);

  // ── Submit ──────────────────────────────────────────────────────
  const submit = useMutation({
    mutationFn: () => {
      if (!prompt.trim()) throw new Error("请先写一段描述");
      if (!selectedModel) throw new Error("请选择一个模型");
      return apiFetch<CreateFreeJobResponse>("/api/v2/generation-jobs", {
        method: "POST",
        body: {
          scenario_code: "free_creation",
          model_code: selectedModel,
          prompt,
          // Opt-in flag. When true, backend rewrites the prompt (in the same
          // language) before sending to the model. Optimized result is NOT
          // returned to the client.
          optimize_prompt: optimizePrompt,
          client_request_id: crypto.randomUUID(),
          reference_asset_ids: references.map((r) => r.asset_id),
          config: {
            output_count: outputCount,
            quality_tier: quality,
            aspect_ratio: aspect,
            style_preset_id: styleId,
          },
        },
      });
    },
    onSuccess: (data) => {
      setResults(data.result_assets || []);
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["assets"] });
      toast(`扣 ${data.cost_credits} 点，余额 ${data.credit_balance_after}`);
    },
    onError: (error: Error) => {
      toast(friendlyError(error.message, "generation"));
    },
  });

  // Download is a native <a download> on the ResultCard; preview is delegated
  // through the global Lightbox via useLightboxStore.show. No per-card
  // action handler needed anymore.

  // Use real lookups (currentModel) to constrain aspect choices.
  const aspectChoices: AspectRatio[] =
    (currentModel?.supported_aspects && currentModel.supported_aspects.length > 0
      ? currentModel.supported_aspects
      : ["1:1", "3:4", "4:3", "16:9", "9:16"]) as AspectRatio[];

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(400px,0.9fr)] xl:items-stretch">
      {/* Left: creation controls. xl:h-... pairs with the right panel so the
          two sections look visually balanced; left scrolls internally if its
          content overflows so the sticky right side stays aligned. */}
      <section className="flex flex-col gap-4 rounded-lg border border-border bg-card p-5 xl:h-[calc(100vh-120px)] xl:overflow-y-auto">
        <PromptBox
          value={prompt}
          onChange={setPrompt}
          optimize={optimizePrompt}
          onOptimizeChange={setOptimizePrompt}
        />

        <ReferenceStrip
          references={references}
          libraryAssets={assets}
          maxUploadMB={
            (selectedModel && MAX_REFERENCE_MB[selectedModel]) || 10
          }
          onRemove={(id) =>
            setReferences((prev) => prev.filter((r) => r.asset_id !== id))
          }
          onPickFromLibrary={(picked) =>
            setReferences((prev) => [...prev, picked])
          }
          onUpload={uploadReferenceFile}
        />

        {/* Style preset section with category chips */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground/85">
              风格预设
            </p>
            <div className="flex flex-wrap gap-1">
              {styleCategories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setStyleCategory(c)}
                  className={cn(
                    "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                    c === styleCategory
                      ? "border-accent bg-accent text-accent-foreground"
                      : "border-border bg-card text-muted-foreground hover:text-foreground",
                  )}
                >
                  {STYLE_CAT_LABEL[c] ?? c}
                </button>
              ))}
            </div>
          </div>
          <StyleCarousel styles={visibleStyles} selectedId={styleId} onSelect={setStyleId} />
        </div>

        {/* Compact params row: model + aspect (count goes to submit bar) */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FieldInline label="模型">
            <SelectNative
              value={selectedModel ?? ""}
              disabled={models.length === 0}
              onChange={(e) => setSelectedModel(e.target.value)}
            >
              {models.length === 0 && <option value="">暂无可用模型</option>}
              {models.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.display_name}
                </option>
              ))}
            </SelectNative>
          </FieldInline>
          <FieldInline label="比例">
            <SelectNative
              value={aspect}
              onChange={(e) => setAspect(e.target.value as AspectRatio)}
            >
              {aspectChoices.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </SelectNative>
          </FieldInline>
        </div>

        {/* Submit bar (Lumi P3): elevated surface to match Prompt Hero visual
            family. Stepper left, cost+CTA right, cost number gets t-h2 weight
            so the price is unmistakable before users hit generate. */}
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-border bg-card-elev px-5 py-4 shadow-button-rest">
          <CountStepper value={outputCount} onChange={setOutputCount} />
          <div className="ml-auto flex items-center gap-4">
            <div className="flex flex-col items-end gap-0.5">
              <span className="t-eyebrow">预估</span>
              <span className="t-h2 flex items-center gap-1 tabular-nums text-accent">
                {estimateCost}
                <Diamond className="h-3.5 w-3.5" />
              </span>
            </div>
            <Button
              type="button"
              size="lg"
              disabled={submit.isPending || !prompt.trim() || !selectedModel}
              onClick={() => submit.mutate()}
            >
              {submit.isPending ? "生成中…" : "✨ 开始创作"}
            </Button>
          </div>
        </div>
      </section>

      {/* Right: result panel — sticky on xl so it stays visible while scrolling controls */}
      <section className="flex flex-col rounded-lg border border-border bg-card xl:sticky xl:top-4 xl:h-[calc(100vh-120px)]">
        <header className="flex items-center justify-between border-b border-border px-5 py-4">
          <h3 className="text-sm font-semibold tracking-tight">生成结果</h3>
          {results.length > 0 && (
            <span className="font-mono text-xs text-muted-foreground">
              {results.length} 张
            </span>
          )}
        </header>
        <div className="flex-1 overflow-y-auto p-4">
          {submit.isPending ? (
            <EmptyResultLoading />
          ) : results.length === 0 ? (
            <EmptyResultGhosts />
          ) : (
            <div
              className={cn(
                "grid gap-4",
                results.length === 1
                  ? "grid-cols-1 justify-items-center"
                  : "grid-cols-1 md:grid-cols-2",
              )}
            >
              {results.map((asset) => (
                <ResultCard key={asset.asset_id} asset={asset} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// EmptyResultGhosts (Lumi P3): 4 ghost cards in a 2×2 grid that quietly
// breathe — hints at the result panel's purpose without occupying it
// with chrome. Subtle accent-tinted gradient so it reads as "future
// content" rather than "broken/empty".
function EmptyResultGhosts() {
  return (
    <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-5 px-6 text-center">
      <div className="grid w-full max-w-[280px] grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="result-ghost aspect-[4/5] rounded-md"
            style={{ animationDelay: `${i * 220}ms` }}
          />
        ))}
      </div>
      <div className="space-y-1.5">
        <p className="t-body font-medium text-foreground">你的作品会出现在这里</p>
        <p
          className="t-small mx-auto max-w-[300px] leading-relaxed"
          style={{ textWrap: "balance" }}
        >
          在左侧写下你想要的画面，挑一个风格，点「开始创作」。
        </p>
      </div>
    </div>
  );
}

function EmptyResultLoading() {
  return (
    <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-3 px-6 text-center">
      <div className="grid h-16 w-16 place-items-center rounded-full border border-border bg-card text-muted-foreground">
        <Loader2 className="h-7 w-7 animate-spin" />
      </div>
      <p className="t-body font-medium text-foreground">正在生成…</p>
      <p
        className="t-small max-w-[300px] leading-relaxed"
        style={{ textWrap: "balance" }}
      >
        AI 模型出图通常需要 1-5 分钟。你可以切去其他模块，任务在后台继续；完成后到「任务中心」查看结果。
      </p>
    </div>
  );
}

function FieldInline({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground/85">
        {label}
      </span>
      {children}
    </label>
  );
}

function CountStepper({
  value,
  onChange,
  min = 1,
  max = 8,
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
}) {
  const set = (n: number) => onChange(Math.max(min, Math.min(max, n)));
  return (
    <label className="flex items-center gap-2">
      <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground/85">
        张数
      </span>
      <div className="inline-flex items-center overflow-hidden rounded-md border border-border bg-card">
        <button
          type="button"
          aria-label="减少"
          onClick={() => set(value - 1)}
          disabled={value <= min}
          className="grid h-8 w-8 place-items-center text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground disabled:opacity-40"
        >
          <Minus className="h-3 w-3" />
        </button>
        <input
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={(e) => set(Number(e.target.value || min))}
          className="h-8 w-10 bg-transparent text-center font-mono text-sm tabular-nums text-foreground outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        <button
          type="button"
          aria-label="增加"
          onClick={() => set(value + 1)}
          disabled={value >= max}
          className="grid h-8 w-8 place-items-center text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground disabled:opacity-40"
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>
    </label>
  );
}

// Shared file helpers for reference upload.
function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = URL.createObjectURL(file);
  });
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result ?? "");
      const comma = dataUrl.indexOf(",");
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(file);
  });
}
